import Alert from '@eduzz/houston-ui/Alert';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import styles from './styles';

import type { AnticipationData } from '../AnticipationPeriodModal';

interface SuggestionAnticipationModalProps {
  open: boolean;
  setClose: () => void;
  suggestionAnticipationValue: number;
  loadAnticipationData: () => void;
  anticipationData: AnticipationData;
}

function SuggestionAnticipationModal({
  open,
  setClose,
  suggestionAnticipationValue,
  loadAnticipationData,
  anticipationData,
}: SuggestionAnticipationModalProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const classes = styles();

  const [changePeriod] = useCallbackObservable(() => {
    setIsLoading(true);
    const fromSuggestion = true;
    return financialStatementService.setAnticipationPeriod(suggestionAnticipationValue, fromSuggestion).pipe(
      tap(
        () => {
          setIsLoading(false);
          loadAnticipationData();
          toast.success(
            `Alteração realizada com sucesso! Sua antecipação automática está configurada para ${suggestionAnticipationValue} dias após crédito em conta.`
          );
        },
        (err) => {
          setIsLoading(false);
          toast.error(err);
        }
      )
    );
  }, [suggestionAnticipationValue]);

  const handleClose = () => {
    if (!showSuccess) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Sugestão de Antecipação',
        label: 'Sugestão de antecipação rejeitada',
      });
    }
    setClose();
  };

  const handleAcceptSuggestion = () => {
    changePeriod();
    setShowSuccess(true);
  };

  const handleSuccessClose = () => {
    setClose();
    setTimeout(() => setShowSuccess(false), 300);
  };

  const textSuggestionButton = () => {
    if (isLoading) return 'Carregando...';
    if (showSuccess) return 'OK';
    return 'Habilitar agora';
  };

  return (
    <Modal
      open={open}
      setClose={handleClose}
      textTitle={!showSuccess ? 'Sugestão para antecipação de valores' : ''}
      textReturnButton='Agora não'
      textProceedButton={textSuggestionButton()}
      size={!showSuccess ? 'sm' : 'xs'}
      hideReturnButton={showSuccess}
      handleProceedClick={!showSuccess ? handleAcceptSuggestion : handleSuccessClose}
    >
      {!showSuccess && (
        <>
          <p>
            Com base no seu histórico de antecipações dos últimos 90 dias, vimos que a melhor opção para o seu perfil é a antecipação automática a
            cada
          </p>{' '}
          <span className={classes.periodDays}>{suggestionAnticipationValue} dias</span>
          {anticipationData?.has_promotional_fee && (
            <Alert type='warning' className={classes.alert} closeIcon={false}>
              Ao continuar, você deixa de ter direito à taxa promocional no valor de {anticipationData?.custom_fee?.value}% e passa a receber os
              valores de acordo com o novo prazo configurado e taxa comum de 2.99% + 0.1% por dia antecipado.
            </Alert>
          )}
          <Alert className={classes.alert} closeIcon={false}>
            Os ganhos futuros com data de crédito superior a {suggestionAnticipationValue} dias serão creditados em até 24h.
          </Alert>
          <p>Deseja habilitar esse período de antecipação automática agora?</p>
        </>
      )}

      {showSuccess && (
        <div className={classes.success}>
          <img width={64} height={63} src='/img/success_icon.svg' alt='Ícone verde de sucesso' />
          <span>Parabéns por habilitar a antecipação automática!</span>
        </div>
      )}
    </Modal>
  );
}

export default SuggestionAnticipationModal;
